import React, { useEffect, useMemo, useState } from "react";
import api from "../utils/api";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";

function calcTotal(qtyExpr) {
  const s = String(qtyExpr || "")
    .toLowerCase()
    .replaceAll(" ", "")
    .replaceAll("x", "*");

  if (s.includes("*")) {
    const [a, b] = s.split("*");
    const n1 = Number(a);
    const n2 = Number(b);
    if (Number.isFinite(n1) && Number.isFinite(n2)) return n1 * n2;
  }

  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
}

function toYMD(val) {
  const s = String(val || "").slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  return "";
}

function toArray(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.results)) return data.results;
  return [];
}

function getMachineValue(m) {
  if (typeof m === "string") return m;
  return (
    m?.machine_name ||
    m?.machineName ||
    m?.name ||
    m?.machine ||
    m?.code ||
    ""
  );
}

function getItemCode(it) {
  return it?.item_code || it?.itemCode || it?.code || "";
}

function getItemName(it) {
  return it?.item_name || it?.itemName || it?.name || "";
}

function getItemUnit(it) {
  return it?.unit || it?.uom || it?.item_unit || "";
}

function getItemType(it) {
  return String(
    it?.item_type || it?.itemType || it?.type || it?.department || ""
  ).toLowerCase();
}

function getEmpCode(emp) {
  return emp?.emp_code || emp?.empCode || emp?.code || "";
}

function getEmpName(emp) {
  return emp?.emp_name || emp?.empName || emp?.name || "";
}

export default function EditPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [date, setDate] = useState("");
  const [shift, setShift] = useState("Day");
  const [checkedBy, setCheckedBy] = useState("");
  const [receivedBy, setReceivedBy] = useState("");

  const [items, setItems] = useState([]);
  const [machines, setMachines] = useState([]);
  const [employees, setEmployees] = useState([]);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLoading(true);

        const [itemsRes, machinesRes, empRes] = await Promise.all([
          api.get("/api/items"),
          api.get("/api/machines?type=extruder"),
          api.get("/api/employees"),
        ]);

        const itemsArr = toArray(itemsRes.data);
        const machineArr = toArray(machinesRes.data);
        const empArr = toArray(empRes.data);

        let headerObj = null;

        try {
          const hRes = await api.get(`/api/production/headers/${id}`);
          headerObj = hRes.data;
        } catch {
          const listRes = await api.get("/api/production/headers");
          const headersArr = toArray(listRes.data);
          headerObj =
            headersArr.find((x) => String(x.headerId) === String(id)) || null;
        }

        const linesRes = await api.get(`/api/production/headers/${id}/lines`);
        const linesArr = toArray(linesRes.data);

        if (!alive) return;

        setItems(itemsArr);
        setMachines(machineArr);
        setEmployees(empArr);

        setDate(toYMD(headerObj?.productionDate));
        setShift(headerObj?.shift || "Day");
        setCheckedBy(headerObj?.checkedBy || "");
        setReceivedBy(headerObj?.receivedBy || "");

        const mapped = linesArr.map((l) => {
          const machine = l.machineName ?? l.machineNo ?? l.machine_name ?? "";
          const itemCode = l.itemCode ?? l.item_code ?? "";
          const itemName = l.itemName ?? l.item_name ?? "";
          const unit = l.unit ?? "";
          const qty = l.quantityExpr ?? l.quantity_expr ?? "";
          const total =
            Number(l.totalQty ?? l.total_qty ?? 0) || calcTotal(qty);

          return {
            lineId: l.lineId ?? l.id ?? null,
            machine,
            itemCode,
            itemName,
            unit,
            qty,
            total,
          };
        });

        setRows(
          mapped.length
            ? mapped
            : [
                {
                  machine: "",
                  itemCode: "",
                  itemName: "",
                  unit: "",
                  qty: "",
                  total: 0,
                },
              ]
        );
      } catch (e) {
        alert(
          e?.response?.data?.message ||
            e?.response?.data?.error ||
            e.message ||
            "Failed to load entry"
        );
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [id]);

  const extruderItems = useMemo(() => {
    return items.filter((i) => {
      const type = getItemType(i);
      if (!type) return true;
      return type.includes("extruder") || type.includes("ext");
    });
  }, [items]);

  const itemByCode = useMemo(() => {
    const map = new Map();
    extruderItems.forEach((i) => {
      const code = getItemCode(i);
      if (code) {
        map.set(code, {
          code,
          name: getItemName(i),
          unit: getItemUnit(i),
        });
      }
    });
    return map;
  }, [extruderItems]);

  const machineOptions = useMemo(() => {
    return machines
      .map((m) => {
        const value = getMachineValue(m);
        return value ? { value, label: value } : null;
      })
      .filter(Boolean);
  }, [machines]);

  const itemCodeOptions = useMemo(() => {
    return extruderItems
      .map((i) => {
        const code = getItemCode(i);
        const name = getItemName(i);
        if (!code) return null;

        return {
          value: code,
          label: code,
          searchText: `${code} ${name}`.toLowerCase(),
        };
      })
      .filter(Boolean);
  }, [extruderItems]);

  const employeeOptions = useMemo(() => {
    return employees
      .map((emp) => {
        const empCode = getEmpCode(emp);
        const empName = getEmpName(emp);

        if (!empName) return null;

        return {
          value: empName,
          label: empName,
          searchText: `${empName} ${empCode}`.toLowerCase(),
        };
      })
      .filter(Boolean);
  }, [employees]);

  const grandTotal = rows.reduce((sum, r) => sum + (Number(r.total) || 0), 0);

  function updateRow(index, key, value) {
    setRows((prev) => {
      const copy = [...prev];
      const r = { ...copy[index], [key]: value };

      if (key === "itemCode") {
        const selectedItem = itemByCode.get(value);
        r.itemName = selectedItem?.name || "";
        r.unit = selectedItem?.unit || "";
      }

      if (key === "qty") {
        r.total = calcTotal(value);
      }

      copy[index] = r;
      return copy;
    });
  }

  function addRow() {
    setRows((prev) => [
      ...prev,
      {
        machine: "",
        itemCode: "",
        itemName: "",
        unit: "",
        qty: "",
        total: 0,
      },
    ]);
  }

  function removeRow(index) {
    setRows((prev) =>
      prev.length === 1 ? prev : prev.filter((_, i) => i !== index)
    );
  }

  async function saveUpdate() {
    const lines = rows
      .map((r) => ({
        lineId: r.lineId || undefined,
        machineName: r.machine,
        itemCode: r.itemCode,
        itemName: r.itemName,
        unit: r.unit,
        quantityExpr: r.qty,
        totalQty: calcTotal(r.qty),
      }))
      .filter(
        (x) =>
          x.machineName &&
          x.itemCode &&
          x.itemName &&
          x.unit &&
          x.quantityExpr &&
          x.totalQty > 0
      );

    if (lines.length === 0) return alert("Fill at least 1 complete row.");

    try {
      await api.put(`/api/production/headers/${id}`, {
        productionDate: date,
        shift,
        checkedBy,
        receivedBy,
        lines,
      });

      alert("Updated successfully");
      navigate("/extruder");
    } catch (err) {
      alert(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err.message ||
          "Update failed"
      );
    }
  }

  const customSelectStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: "31px",
      height: "31px",
      border: "1px solid #ced4da",
      boxShadow: "none",
      borderRadius: "0.25rem",
    }),
    valueContainer: (provided) => ({
      ...provided,
      height: "31px",
      padding: "0 8px",
    }),
    input: (provided) => ({
      ...provided,
      margin: "0px",
      padding: "0px",
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      height: "31px",
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      padding: "4px",
    }),
    clearIndicator: (provided) => ({
      ...provided,
      padding: "4px",
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      marginTop: "6px",
      marginBottom: "6px",
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
      fontSize: "14px",
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#6c757d",
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "#212529",
    }),
  };

  if (loading) return <div className="p-3">Loading...</div>;

  return (
    <div className="container-fluid p-3">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h2 className="mb-0">Edit Extruder Entry (ID: {id})</h2>
        <div className="d-flex gap-2">
          <button className="btn btn-dark" onClick={saveUpdate}>
            Save Changes
          </button>
          <button
            className="btn btn-outline-secondary"
            onClick={() => navigate("/extruder")}
          >
            Close
          </button>
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-2">
              <label className="form-label">Date</label>
              <input
                type="date"
                className="form-control"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>

            <div className="col-md-2">
              <label className="form-label">Shift</label>
              <select
                className="form-select"
                value={shift}
                onChange={(e) => setShift(e.target.value)}
              >
                <option>Day</option>
                <option>Night</option>
              </select>
            </div>

            <div className="col-md-4">
              <label className="form-label">Checked By</label>
              <Select
                options={employeeOptions}
                value={
                  employeeOptions.find((opt) => opt.value === checkedBy) || null
                }
                onChange={(selected) =>
                  setCheckedBy(selected ? selected.value : "")
                }
                placeholder="-- Select Employee --"
                isClearable
                isSearchable
                styles={customSelectStyles}
                noOptionsMessage={() => "No employee found"}
                filterOption={(option, inputValue) => {
                  const text =
                    `${option.label} ${option.data.searchText || ""}`.toLowerCase();
                  return text.includes(String(inputValue).toLowerCase());
                }}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>

            <div className="col-md-4">
              <label className="form-label">Received By</label>
              <Select
                options={employeeOptions}
                value={
                  employeeOptions.find((opt) => opt.value === receivedBy) || null
                }
                onChange={(selected) =>
                  setReceivedBy(selected ? selected.value : "")
                }
                placeholder="-- Select Employee --"
                isClearable
                isSearchable
                styles={customSelectStyles}
                noOptionsMessage={() => "No employee found"}
                filterOption={(option, inputValue) => {
                  const text =
                    `${option.label} ${option.data.searchText || ""}`.toLowerCase();
                  return text.includes(String(inputValue).toLowerCase());
                }}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="table-responsive">
          <table className="table table-bordered mb-0 align-middle">
            <thead className="table-light">
              <tr>
                <th style={{ width: 50 }}>Sr</th>
                <th style={{ width: 220 }}>Machine</th>
                <th style={{ width: 180 }}>Item Code</th>
                <th>Item Name</th>
                <th style={{ width: 90 }}>Unit</th>
                <th style={{ width: 140 }}>Qty</th>
                <th style={{ width: 90 }}>Total</th>
                <th style={{ width: 160 }}>Action</th>
              </tr>
            </thead>

            <tbody>
              {rows.map((r, idx) => (
                <tr key={r.lineId ?? idx}>
                  <td className="text-center align-middle">{idx + 1}</td>

                  <td className="align-middle">
                    <Select
                      options={machineOptions}
                      value={
                        machineOptions.find((opt) => opt.value === r.machine) ||
                        null
                      }
                      onChange={(selected) =>
                        updateRow(idx, "machine", selected ? selected.value : "")
                      }
                      placeholder="-- Select --"
                      isClearable
                      isSearchable
                      styles={customSelectStyles}
                      noOptionsMessage={() => "No machine found"}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </td>

                  <td className="align-middle">
                    <Select
                      options={itemCodeOptions}
                      value={
                        itemCodeOptions.find((opt) => opt.value === r.itemCode) ||
                        null
                      }
                      onChange={(selected) =>
                        updateRow(
                          idx,
                          "itemCode",
                          selected ? selected.value : ""
                        )
                      }
                      placeholder="-- Select --"
                      isClearable
                      isSearchable
                      styles={customSelectStyles}
                      noOptionsMessage={() => "No item found"}
                      filterOption={(option, inputValue) => {
                        const text =
                          `${option.label} ${option.data.searchText || ""}`.toLowerCase();
                        return text.includes(String(inputValue).toLowerCase());
                      }}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </td>

                  <td className="align-middle">
                    <input
                      className="form-control form-control-sm"
                      value={r.itemName}
                      readOnly
                    />
                  </td>

                  <td className="align-middle">
                    <input
                      className="form-control form-control-sm"
                      value={r.unit}
                      readOnly
                    />
                  </td>

                  <td className="align-middle">
                    <input
                      className="form-control form-control-sm"
                      placeholder="9*100"
                      value={r.qty}
                      onChange={(e) => updateRow(idx, "qty", e.target.value)}
                    />
                  </td>

                  <td className="text-end fw-bold align-middle">{r.total}</td>

                  <td className="align-middle">
                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => removeRow(idx)}
                      >
                        Remove
                      </button>
                      <button
                        className="btn btn-sm btn-outline-dark"
                        onClick={addRow}
                      >
                        + Add
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="card-footer d-flex justify-content-between">
          <div>
            <b>Total Records:</b> {rows.length}
          </div>
          <div>
            <b>Grand Total:</b> {grandTotal}
          </div>
        </div>
      </div>
    </div>
  );
}